import { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import type { AggregatedMwrrItem } from '@/api/types';

type SortField = 'period' | 'return';
type SortDirection = 'asc' | 'desc';

interface AggregatedReturnsTableProps {
    /** Array of aggregated MWRR items for table display */
    items: AggregatedMwrrItem[];
}

/**
 * Get value color based on return percentage.
 * Positive is green, negative is red, null is neutral.
 */
function getValueColorClass(value: number | null): string {
    if (value === null) return 'text-gray-400 dark:text-gray-500';
    if (value > 0) return 'text-green-600 dark:text-green-400';
    if (value < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
}

/**
 * Format percentage with sign.
 */
function formatPercentage(value: number | null): string {
    if (value === null) return '-';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
}

/**
 * Sort indicator icon component.
 */
function SortIcon({ field, currentField, direction }: { field: SortField; currentField: SortField; direction: SortDirection }) {
    if (field !== currentField) {
        return (
            <span className="ml-1 inline-flex flex-col opacity-30">
                <ChevronUpIcon className="h-3 w-3 -mb-1" />
                <ChevronDownIcon className="h-3 w-3" />
            </span>
        );
    }
    return direction === 'asc' ? (
        <ChevronUpIcon className="h-4 w-4 ml-1 inline" />
    ) : (
        <ChevronDownIcon className="h-4 w-4 ml-1 inline" />
    );
}

/**
 * Aggregated returns table component displaying portfolio-level MWRR data in tabular format.
 * Columns: Period | MWRR % | Ledger Count
 * Features sortable columns with sort indicators.
 */
export function AggregatedReturnsTable({ items }: AggregatedReturnsTableProps) {
    const [sortField, setSortField] = useState<SortField>('period');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const metricLabel = 'MWRR';

    // Get return value
    const getReturnValue = (item: AggregatedMwrrItem): number | null => {
        return item.mwrr;
    };

    // Sort data
    const sortedItems = useMemo(() => {
        const sorted = [...items];
        sorted.sort((a, b) => {
            let aVal: number | string | null;
            let bVal: number | string | null;

            switch (sortField) {
                case 'period':
                    aVal = a.periodEnd;
                    bVal = b.periodEnd;
                    break;
                case 'return':
                    aVal = getReturnValue(a);
                    bVal = getReturnValue(b);
                    break;
                default:
                    aVal = a.periodEnd;
                    bVal = b.periodEnd;
            }

            // Handle nulls
            if (aVal === null) {
                if (bVal === null) return 0;
                return sortDirection === 'asc' ? 1 : -1;
            }
            if (bVal === null) return sortDirection === 'asc' ? -1 : 1;

            // Compare
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [items, sortField, sortDirection]);

    // Handle column header click
    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    // Header button style
    const headerButtonClass = "flex items-center cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors";

    if (items.length === 0) {
        return (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                No data available
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                            <button
                                className={headerButtonClass}
                                onClick={() => handleSort('period')}
                            >
                                Period
                                <SortIcon field="period" currentField={sortField} direction={sortDirection} />
                            </button>
                        </th>
                        <th className="text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                            <button
                                className={`${headerButtonClass} ml-auto`}
                                onClick={() => handleSort('return')}
                            >
                                {metricLabel} %
                                <SortIcon field="return" currentField={sortField} direction={sortDirection} />
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedItems.map((item, index) => {
                        const returnValue = getReturnValue(item);
                        return (
                            <tr
                                key={item.periodEnd}
                                className={`border-b border-gray-100 dark:border-gray-800 ${
                                    index % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''
                                }`}
                            >
                                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                    {item.periodLabel}
                                </td>
                                <td className={`px-4 py-3 text-right font-semibold ${getValueColorClass(returnValue)}`}>
                                    {formatPercentage(returnValue)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
